'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { Namespace } from '../config';
import type { I18nInstance, TranslationFunction } from '../types';
import type { SimpleI18n } from './SimpleI18n';

interface I18nContextValue {
  i18n: I18nInstance;
  t: TranslationFunction;
  ready: boolean;
  language: string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

/**
 * The active SimpleI18n instance. Set exactly once at module-load time by
 * the platform composer (`packages/i18n/src/web/index.ts` or
 * `packages/i18n/src/mobile/index.ts`). All hooks read from this binding.
 */
let activeInstance: SimpleI18n | null = null;

export function setActiveI18nInstance(instance: SimpleI18n): void {
  activeInstance = instance;
}

function getActiveInstance(): SimpleI18n {
  if (!activeInstance) {
    throw new Error(
      '@splitfiesta/i18n: no active i18n instance. Import from ' +
        '"@splitfiesta/i18n/web" (or /mobile) to compose the platform-specific singleton.'
    );
  }
  return activeInstance;
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const i18n = getActiveInstance();
  const [state, setState] = useState({
    ready: false,
    language: i18n.language,
    version: 0,
  });

  useEffect(() => {
    const loadInitial = async () => {
      await i18n.loadNamespaces(['common', 'errors']);
      i18n.isInitialized = true;
      setState(prev => ({ ...prev, ready: true }));
    };

    loadInitial();

    const unsubscribe = i18n.onChange(() => {
      setState(prev => ({
        ready: true,
        language: i18n.language,
        version: prev.version + 1,
      }));
    });

    return unsubscribe;
  }, [i18n]);

  const t = useCallback(
    (key: string, options?: any) => i18n.t(key, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n, state.version]
  );

  const value = useMemo(
    () => ({
      i18n,
      t,
      ready: state.ready,
      language: state.language,
    }),
    [i18n, t, state.ready, state.language]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(namespace?: Namespace) {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }

  const { i18n, t, ready, language } = context;
  const [nsReady, setNsReady] = useState(false);

  useEffect(() => {
    setNsReady(false);

    if (!namespace || namespace === 'common') {
      setNsReady(true);
      return;
    }

    const loadNs = async () => {
      if (i18n.hasResourceBundle(language, namespace)) {
        setNsReady(true);
        return;
      }

      await i18n.loadNamespaces([namespace]);
      setNsReady(true);
    };

    loadNs();
  }, [namespace, i18n, language]);

  const namespacedT = useCallback(
    (key: string, options?: any) => {
      if (key.includes(':')) {
        return t(key, options);
      }
      const fullKey = namespace ? `${namespace}:${key}` : key;
      return t(fullKey, options);
    },
    [t, namespace]
  );

  return {
    t: namespacedT,
    i18n,
    ready: ready && nsReady,
    language,
  };
}

export function useNamespace(namespace: Namespace) {
  const context = useContext(I18nContext);
  if (!context) return false;

  const { i18n, language } = context;

  const [loadedForLanguage, setLoadedForLanguage] = useState<string | null>(
    () => {
      return i18n.hasResourceBundle(language, namespace) ? language : null;
    }
  );

  const isLoaded = loadedForLanguage === language;

  useEffect(() => {
    if (i18n.hasResourceBundle(language, namespace)) {
      setLoadedForLanguage(language);
      return;
    }

    setLoadedForLanguage(null);

    let cancelled = false;

    const loadNamespace = async () => {
      await i18n.loadNamespaces([namespace]);
      if (!cancelled) {
        setLoadedForLanguage(language);
      }
    };

    loadNamespace();

    return () => {
      cancelled = true;
    };
  }, [namespace, i18n, language]);

  return isLoaded;
}

export function usePreloadNamespace(namespace: Namespace | Namespace[]) {
  const context = useContext(I18nContext);
  if (!context) return;

  const { i18n, language } = context;

  useEffect(() => {
    const namespaces = Array.isArray(namespace) ? namespace : [namespace];
    i18n.loadNamespaces(namespaces);
  }, [namespace, i18n, language]);
}

// Note: useCurrency / useDate are re-exported from ../hooks via the
// core barrel (./index.ts) to avoid a circular import — those hooks
// call useTranslation, which lives in this file.
